import React from 'react';
import DisplayField, { FieldGroup } from '../UI/DisplayField';
import { toCurrency } from '../../utils/adminMetrics';

// The pieces a loan's screens share, so viewing a loan (LoanDetails) and
// editing it (LoanEditForm) show the same sections, in the same order and
// the same look.

const CARD_TONES = {
  // The loan's own figures.
  yellow: {
    box: 'bg-yellow-50/50 dark:bg-slate-800 border-yellow-100 dark:border-slate-700',
    rule: 'border-yellow-100 dark:border-slate-700',
    icon: 'text-yellow-600 dark:text-yellow-400',
    title: 'text-yellow-800 dark:text-yellow-200',
  },
  // Documents, notes and the rest.
  white: {
    box: 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700',
    rule: 'border-gray-100 dark:border-slate-700',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-gray-800 dark:text-slate-100',
  },
};

/**
 * A section of a loan screen: a card with an icon and a heading.
 *   Icon      an IconLibrary icon component (e.g. CashIcon)
 *   tone      'yellow' (Loan Details) or 'white' (Documents, notes)
 *   iconClass optional: the icon's colour instead of the tone's
 */
export const LoanSection = ({ Icon, title, tone = 'white', iconClass = '', className = '', children }) => {
  const t = CARD_TONES[tone] || CARD_TONES.white;
  return (
    <div className={`rounded-lg p-5 shadow-sm border ${t.box} ${className}`}>
      <div className={`flex items-center mb-4 pb-2 border-b ${t.rule}`}>
        {Icon && <Icon className={`w-5 h-5 mr-2 ${iconClass || t.icon}`} />}
        <h3 className={`text-base font-medium ${t.title}`}>{title}</h3>
      </div>
      {children}
    </div>
  );
};

// The groups inside Loan Details: the Loan group across the row, the others
// side by side under it.
export const LOAN_GROUPS_GRID = 'grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4';

// A loan that is approved or past approval: the API's loan.state.approved,
// or, on a loan sent without its state, the same statuses as the API's
// APPROVED_STATUSES (utils/loanState.js).
const APPROVED_STATUSES = ['Approved', 'Outstanding', 'Overdue', 'Paid Off', 'Bad Debt', 'Written off'];
export const isApprovedLoan = (loan) => (typeof loan?.state?.approved === 'boolean'
  ? loan.state.approved
  : APPROVED_STATUSES.includes(loan?.status));

// What the client repays. Penalties and what is still due only mean
// something once the loan is approved, so they show only then.
//   monthly  the monthly payment, as shown
//   totalDue what is still owed after penalties, as shown
export const RepaymentGroup = ({ loan, monthly, totalDue }) => {
  const penalty = Number(loan?.penalty_interest) || 0;
  return (
    <FieldGroup title="Repayment" tone="violet">
      <DisplayField label="Monthly Payment">{monthly}</DisplayField>
      {isApprovedLoan(loan) && (
        <>
          <DisplayField label="Penalty Interest">{toCurrency(penalty)}</DisplayField>
          <DisplayField label="Total Due (After Penalties)" tone={penalty > 0 ? 'alert' : null}>{totalDue}</DisplayField>
        </>
      )}
    </FieldGroup>
  );
};

// How the client wants the money once approved (chosen with the references).
export const PayoutGroup = ({ loan }) => (
  <FieldGroup title="Payout" tone="teal">
    <DisplayField label="Payout Method">{loan?.payout_method || 'Not chosen yet'}</DisplayField>
    {loan?.payout_method === 'eWallet' && (
      <DisplayField label="eWallet Number">{loan.payout_ewallet_number || '-'}</DisplayField>
    )}
    {loan?.payout_method === 'EFT' && (
      <>
        <DisplayField label="Bank">{loan.payout_bank_name || '-'}</DisplayField>
        <DisplayField label="Branch Code">{loan.payout_branch_code || '-'}</DisplayField>
        <DisplayField label="Account Number">{loan.payout_account_number || '-'}</DisplayField>
        <DisplayField label="Account Type">{loan.payout_account_type || '-'}</DisplayField>
        <DisplayField label="Account Holder">{loan.payout_account_holder || '-'}</DisplayField>
      </>
    )}
  </FieldGroup>
);
